import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import React, {FC, useCallback, useContext, useState} from 'react';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {PanelBankUpdaterContext} from '../../state/panelbank/context';
import {RunQueryContext} from '../../state/runs/context';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../util/panelbankFlow';
import {ImageExportType} from '../../util/panelExport';
import {getPanelSpec} from '../../util/panelTypes';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {MovePanelSubmenu} from './MovePanelSubmenu';
import {usePanelContext} from './PanelContextProvider';
import {PanelCopyLinkMenuItem} from './PanelCopyLinkMenuItem';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {PanelExportMenu} from './PanelExportMenu';
import {PanelExportModalContainer} from './PanelExportModalContainer';
import {
  PanelShareInReportMenuItem,
  PanelShareInReportModalContainer,
} from './PanelShareInReportMenuItem';
import {useSectionsForMovePanel} from './useSectionsForMovePanel';

export enum ExportType {
  API,
  CSV,
  Image,
}

interface PanelActionsOverflowProps {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
}

export const PanelActionsMenu: FC<PanelActionsOverflowProps> = ({
  defaultMoveToSectionRef,
  panelSpec,
  panelBankSectionConfigRef,
  setIsOpen,
  isOpen,
}) => {
  const runsContext = useContext(RunQueryContext);
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();
  const {panelRef, trackPanelControls, isReadOnly, isProjectReadOnly} =
    usePanelContext();

  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const [reportID, setReportID] = useState<undefined | string>();
  const [reportName, setReportName] = useState('');

  const sectionsForMovePanel = useSectionsForMovePanel();
  const isMovePanelAllowed =
    runsContext.report == null && sectionsForMovePanel.length > 0;

  const duplicatePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.duplicatePanel
  );

  const deletePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );
  const onRemovePanel = () => {
    deletePanel(panelRef, panelBankConfigRef, workspaceSettingsRef);
    trackPanelControls({action: 'Panel deleted'});
  };
  const {setExportingPanelRefID} = useContext(PanelBankUpdaterContext);
  const isReport = useIsInLoadedReportContext();

  const handleClose = () => {
    setIsOpen(false);
  };

  if (isMobile()) {
    return null;
  }

  return (
    <>
      <PanelExportModalContainer
        exportType={exportType}
        imageExportType={imageExportType}
        panelType={panelSpec.type}
        unsetExportType={unsetExportType}
      />
      {reportID && (
        <PanelShareInReportModalContainer
          reportID={reportID}
          reportName={reportName}
          setReportID={setReportID}
        />
      )}
      <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger>
          <Button
            active={isOpen}
            data-test="panel-action-icon-overflow"
            className="mr-8"
            icon="overflow-horizontal"
            variant="ghost"
            aria-label="More actions"
            size="small"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          {/* The zIndex is needed for reports, otherwise the menu is hidden behind the
        panel */}
          <DropdownMenu.Content style={{zIndex: 1}} align="start">
            {!isProjectReadOnly && !isReport && (
              <DropdownMenu.Item
                data-test="add-to-report-button"
                onClick={() => {
                  trackPanelControls({
                    action: 'Add to report clicked',
                  });
                  setExportingPanelRefID(panelRef.id);
                }}>
                <Icon name="add-to-report" /> Add panel to report
              </DropdownMenu.Item>
            )}
            <PanelCopyLinkMenuItem
              panelBankSectionConfigRef={panelBankSectionConfigRef}
            />
            <PanelExportMenu
              panelSpec={panelSpec}
              setExportType={setExportType}
              setImageExportType={setImageExportType}
            />
            {/* Share panel */}
            {!isReadOnly && !isProjectReadOnly && !isMobile() && !isReport && (
              <PanelShareInReportMenuItem
                setReportID={setReportID}
                setReportName={setReportName}
              />
            )}
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              data-test="panel-action-duplicate"
              onClick={() => {
                duplicatePanel(panelRef);
                handleClose();
                trackPanelControls({action: 'Panel duplicated'});
              }}>
              <Icon name="copy" />
              Duplicate panel
            </DropdownMenu.Item>
            {isMovePanelAllowed &&
              panelBankConfigRef &&
              panelBankSectionConfigRef && (
                <MovePanelSubmenu
                  panelBankConfigRef={panelBankConfigRef}
                  panelBankSectionConfigRef={panelBankSectionConfigRef}
                  eligibleSections={sectionsForMovePanel}
                  defaultMoveToSectionRef={defaultMoveToSectionRef}
                />
              )}
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              className="panel-action-delete"
              onClick={() => {
                onRemovePanel();
                handleClose();
              }}>
              <Icon name="delete" />
              Delete panel
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
};
